import React, { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { api } from "@/redux/api";
import appActions from "@/redux/app/actions";
import companyActions from "@/redux/company/actions";
import facilityActions from "@/redux/facility/actions";
import { useAuthEditable } from "@/hooks/use-auth";
import { buildFacilityPath, buildPortfolioPath } from "@/utils/route-context";
import ServiceRequestDetail from "@/containers/service-request/ServiceRequestDetail";

const buildFacilitySelection = (facility) => ({
  Id: facility.id,
  key: facility.id,
  value: facility.id,
  label: facility.name,
  record: facility,
});

export default function PortfolioServiceRequestDetailPage() {
  const { companyId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userFacilities = useSelector((state) => state.Auth?.user_facilities);
  const canAddNote = useAuthEditable("serviceRequest");
  const canCreateWorkorder = useAuthEditable("workorder");
  const baseListPath = buildPortfolioPath("service-request", { companyId }) || "/switch-view";
  const listPath = `${baseListPath}${location.search || ""}`;

  const loadRequest = useCallback(
    (requestId) => api.getFacilityServiceRequest(requestId),
    []
  );
  const addNote = useCallback(
    (requestId, content) => api.addFacilityServiceRequestNote(requestId, content),
    []
  );
  const quoteCost = useCallback(
    (requestId, quotedCost, comment) =>
      api.quoteFacilityServiceRequestCost(requestId, quotedCost, comment),
    []
  );

  const switchToFacilityContext = useCallback(
    (facilityId, fallbackCompanyId = companyId) => {
      const accessibleFacilities = Array.isArray(userFacilities) ? userFacilities : [];
      const facility = accessibleFacilities.find(
        (item) =>
          String(item.id) === String(facilityId) &&
          String(item.company_id) === String(fallbackCompanyId)
      );

      if (!facility) return null;

      dispatch(companyActions.setSelectedCompanyId(Number(facility.company_id)));
      dispatch(facilityActions.SelectedFacility(buildFacilitySelection(facility)));
      dispatch(appActions.setViewMode(0));
      return facility;
    },
    [companyId, dispatch, userFacilities]
  );

  const createWorkorder = useCallback(
    (record) => {
      const facility = switchToFacilityContext(record.facility, record.company_id || companyId);
      if (!facility) return;

      const createPath = buildFacilityPath("create-workorder", {
        companyId: facility.company_id,
        facilityId: facility.id,
      });
      if (!createPath) return;

      const params = new URLSearchParams({
        source: "service-request",
        service_request_id: String(record.id),
        service_request_category_id: String(record.category),
      });
      navigate(`${createPath}?${params.toString()}`);
    },
    [companyId, navigate, switchToFacilityContext]
  );

  const getRelatedWorkorderPath = useCallback(
    (workorder, record) => {
      const facility = switchToFacilityContext(record?.facility, record?.company_id || companyId);
      if (!facility) return "/switch-view";

      return buildFacilityPath(`workorder-detail/${workorder.id}`, {
        companyId: facility.company_id,
        facilityId: facility.id,
      }) || "/switch-view";
    },
    [companyId, switchToFacilityContext]
  );

  return (
    <ServiceRequestDetail
      requestId={id}
      listPath={listPath}
      loadRequest={loadRequest}
      addNote={addNote}
      canAddNote={canAddNote}
      canManageCost={canAddNote}
      quoteCost={quoteCost}
      canCreateWorkorder={canCreateWorkorder}
      createWorkorder={createWorkorder}
      getRelatedWorkorderPath={getRelatedWorkorderPath}
    />
  );
}
